import React, { useEffect } from 'react';
import { AlertTriangle, Hourglass, RotateCcw, X } from 'lucide-react';
import { Button } from '../ui/button';
import ReplyStatusChip from './ReplyStatusChip';
import HeldReplyCard from './HeldReplyCard';
import { SourceNote } from './MessageBubble';
import { fmtWhen, fmtClock, failLineText, waitingLineOf, R16Q_FAIL_CODES, chatLogTagged } from '../../lib/chatsView';
import { r16qUiOn, R16Q_TAG } from '../../lib/r16qSwitch';

/*
 * r16p (design 6.3, 7.1): one of OUR queued replies in the transcript, with its state.
 *   queued            Cancel (DELETE; 409 reply_already_claimed once the worker picked it up - the server's words)
 *   claimed / sending no button: the worker has it
 *   held              the HELD card (Send anyway / Confirm, Cancel)
 *   sent              the "Sent" chip (until the next sync shows it as a message, then the message bubble takes over)
 *   unconfirmed       "We could not confirm this was sent. Check on the phone. It will not be sent again on its own." + "Queue again",
 *                     which asks first and names the double-send risk (the worker still looks for the text first, again=true)
 *   failed            the reason in words (FINAL wording: FAILED is never tried again) + "Queue again" (asks first) when the
 *                     reason allows it
 *   r16q (the merged server's chat request guard, switches necro.ui.r16q.*):
 *   queued + waitingReason  the server's waiting line (waitingMessage: the conversation was last read in Instagram's All tab / an
 *                     unproven tab, or it is now in the requests), instead of the peer-check line: the worker does not open it while
 *                     it waits. Cancel stays (the reply is still QUEUED). Part waitingLine.
 *   failed inbound_out_of_view  the server's sentence ("... Reply by hand on the phone.") and NO "Queue again": a new reply meets the
 *                     same check unless the person writes again (the round-2 review's SF7). Part outOfView.
 *   cancelled         the chip
 *   other             a state this bundle does not know: the chip only, no action (review)
 * The text is the server's normalised text (no newlines in r16p), plain React text.
 */
export const FINAL_NO_REQUEUE = new Set(['owner_changed', 'account_changed', 'conversation_detached', 'group_thread', 'request_not_accepted',
  'thread_restricted', 'slot_mode_off', 'peer_ambiguous',
  // r16q: only while necro.ui.r16q.outOfView is on (requeueOffered below)
  'inbound_out_of_view']);

/** Whether "Queue again" is offered for this reply (an UNCONFIRMED one always; a FAILED one unless its code is final for good). */
export function requeueOffered(r, outOfViewOn = true) {
  if (!r) return false;
  if (r.status === 'unconfirmed') return true;
  if (r.status !== 'failed') return false;
  const code = r.failCode || '';
  return !(FINAL_NO_REQUEUE.has(code) && (outOfViewOn || !R16Q_FAIL_CODES.has(code)));
}

// one [R16Q-UI] line per reply and decision per page load (a poll re-renders the same bubble; it is not a new decision)
const LOGGED = new Set();
function logOnce(key, event, fields) {
  if (LOGGED.has(key)) return;
  LOGGED.add(key);
  chatLogTagged(R16Q_TAG, 'info', event, fields);
}
/** Tests only. */
export function resetReplyBubbleLog() {
  LOGGED.clear();
}

export default function ReplyBubble({ reply, peerUsername, onCancel, onRelease, onRequeue, busy, now }) {
  const r = reply;
  const who = r.byApi ? <SourceNote source="api" /> : r.byAi ? <SourceNote source="ai_edited" /> : <span>you</span>;
  const outOfViewOn = r16qUiOn('outOfView');
  const canRequeue = requeueOffered(r, outOfViewOn);
  const waiting = r16qUiOn('waitingLine') ? waitingLineOf(r) : null;
  const withheld = r.status === 'failed' && outOfViewOn && R16Q_FAIL_CODES.has(r.failCode || '');
  useEffect(() => {
    // deep logging (rpl_ ids and the server's codes only, never a text): what this bubble decided from the r16q fields
    if (waiting) logOnce(`w:${r.id}:${r.waitingReason}`, 'reply waiting line shown', { reply: r.id, reason: r.waitingReason, words: waiting.source });
    if (withheld) logOnce(`f:${r.id}`, 'queue again not offered: final - reply by hand', { reply: r.id, code: r.failCode, words: r.message ? 'server' : 'copy' });
  }, [r.id, r.waitingReason, r.failCode, r.message, waiting, withheld]);
  return (
    <div className="flex justify-end" data-testid={`reply-${r.id}`} data-status={r.status}>
      <div className="min-w-0 w-full max-w-[85%] sm:max-w-[75%]">
        <div className="rounded-2xl rounded-br-md border border-dashed border-primary/35 bg-primary/[0.07] px-3.5 py-2 text-sm text-foreground">
          {typeof r.text === 'string' && r.text !== ''
            ? <div className="whitespace-pre-wrap break-words" data-testid="reply-text">{r.text}</div>
            : <span className="text-xs italic text-muted-foreground">Text removed after the retention period.</span>}
        </div>
        <div className="mt-1 flex flex-wrap items-center justify-end gap-x-2 gap-y-1 px-1 text-[11px] text-muted-foreground">
          <span className="inline-flex items-center gap-1">queued by {who}</span>
          {r.createdAt && <span className="tnum">{fmtClock(r.createdAt)}</span>}
          <ReplyStatusChip status={r.status} compact testid={`reply-status-${r.id}`} />
          {r.status === 'queued' && (
            <Button size="sm" variant="ghost" className="h-7 rounded-full px-2 text-xs text-muted-foreground hover:text-foreground"
              disabled={busy} onClick={() => onCancel(r)} data-testid={`reply-cancel-${r.id}`}>
              <X className="h-3.5 w-3.5" aria-hidden /> Cancel
            </Button>
          )}
        </div>
        {r.status === 'held' && (
          <div className="mt-2"><HeldReplyCard reply={r} peerUsername={peerUsername} onRelease={onRelease} onCancel={onCancel} busy={busy} /></div>
        )}
        {r.status === 'sent' && r.sentAt && (
          <p className="mt-0.5 px-1 text-right text-[11px] text-success">Sent {fmtWhen(r.sentAt, now)}</p>
        )}
        {/* r16pm: the r16p server's reply.confirmPeer flag - no proven peer when it was queued (a name-keyed conversation), so the
            worker opens the conversation once to check who it is with before anything is typed; a HELD "Send to @x?" may follow */}
        {waiting ? (
          // r16q: the server's sentence; the worker will not open the conversation while it waits, so no peer-check line
          <div className="ml-auto mt-1.5 flex w-fit max-w-full items-start gap-2 rounded-xl border border-warning/30 bg-warning/[0.07] px-3 py-2 text-xs leading-relaxed text-foreground/90"
            role="status" data-testid={`reply-waiting-${r.id}`} data-reason={r.waitingReason}>
            <Hourglass className="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning" aria-hidden />
            <span className="text-pretty">{waiting.text}</span>
          </div>
        ) : r.peerCheck && r.status === 'queued' ? (
          <p className="mt-1 px-1 text-right text-[11px] text-muted-foreground text-pretty" data-testid={`reply-peer-check-${r.id}`}>
            Before typing, the worker opens this conversation once to check who it is with (Instagram shows it as Seen).
          </p>
        ) : (r.verifyOpened && (r.status === 'queued' || r.status === 'held') && (
          <p className="mt-1 px-1 text-right text-[11px] text-muted-foreground">The worker opened the thread to check who it is with.</p>
        ))}
        {r.status === 'unconfirmed' && (
          <div className="mt-2 rounded-xl border border-warning/40 bg-warning/10 p-3 text-sm" role="status" data-testid={`unconfirmed-${r.id}`}>
            <p className="flex items-start gap-2 text-foreground">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden />
              <span>We could not confirm this was sent. Check on the phone. It will not be sent again on its own.</span>
            </p>
          </div>
        )}
        {r.status === 'failed' && (
          <p className="mt-1 px-1 text-right text-xs text-danger" data-testid={`reply-fail-${r.id}`}>
            {failLineText(r, outOfViewOn)}
          </p>
        )}
        {canRequeue && onRequeue && (
          <div className="mt-1.5 flex justify-end">
            <Button size="sm" variant="outline" className="tap h-8 rounded-full text-xs" disabled={busy}
              onClick={() => onRequeue(r)} data-testid={`reply-requeue-${r.id}`}>
              <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Queue again
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
